import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import {
    Box,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material'
import StartIcon from '@mui/icons-material/Start'
import FolderCopyTwoToneIcon from '@mui/icons-material/FolderCopyTwoTone'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'

export const Navigation = () => {
    const intl = useIntl()
    const navigate = useNavigate()
    const [collapsed, setCollapsed] = useState(false)
    const [pristine, setPristine] = useState(true)
    const refCollapse = useRef<HTMLButtonElement>(null)
    const refExpand = useRef<HTMLButtonElement>(null)

    const onCollapse = () => {
        setPristine(false)
        setCollapsed(!collapsed)
    }

    useEffect(() => {
        if (collapsed && refExpand.current) {
            refExpand.current.focus()
        }

        if (!pristine && !collapsed && refCollapse.current) {
            refCollapse.current.focus()
        }
    }, [collapsed, pristine])

    const width = collapsed ? 50 : 250
    const listItemIconMinWidth = collapsed ? 24 : 40

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                flexGrow: 0,
                flexShrink: 0,
                flexBasis: `${width}px`,
                flexDirection: 'column',
                transition: 'flex-basis 300ms',
                backgroundColor: '#F2F2F2',
                position: 'sticky',
                top: 0,
                left: 0,
                maxHeight: '100vh',
            }}
            role="presentation"
        >
            <Box
                pt={2}
                pb={1}
                px={collapsed ? 1.75 : 2}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                minHeight={56}
                boxSizing="border-box"
                width={`${width}px`}
            >
                {collapsed ? (
                    <IconButton ref={refExpand} size="medium" onClick={onCollapse}>
                        <Typography variant="h5" color="black">
                            S3
                        </Typography>
                    </IconButton>
                ) : (
                    <Typography variant="h5" sx={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {intl.formatMessage({ defaultMessage: 'S3 Manager' })}
                    </Typography>
                )}

                {!collapsed && (
                    <IconButton ref={refCollapse} onClick={onCollapse} aria-label="collapse menu">
                        <StartIcon sx={{ transform: 'rotate(180deg)' }} />
                    </IconButton>
                )}
            </Box>

            <List sx={{ width: '100%' }}>
                <ListItem disablePadding>
                    <ListItemButton
                        data-testid="nav-buckets"
                        aria-label="navigate to Buckets"
                        onClick={() => navigate('/buckets')}
                    >
                        <ListItemIcon sx={{ minWidth: listItemIconMinWidth }}>
                            <FolderCopyTwoToneIcon />
                        </ListItemIcon>
                        {!collapsed && (
                            <ListItemText
                                sx={{ m: 0 }}
                                primary={intl.formatMessage({
                                    defaultMessage: 'Buckets',
                                })}
                            />
                        )}
                    </ListItemButton>
                </ListItem>
            </List>
            <Divider sx={{ width: '100%' }} />
            <form action="/logout" method="POST" style={{ width: '100%' }}>
                <List sx={{ width: '100%' }}>
                    <ListItem disablePadding>
                        <ListItemButton
                            data-testid="nav-logout"
                            aria-label="Logout"
                            component="button"
                            type="submit"
                        >
                            <ListItemIcon sx={{ minWidth: listItemIconMinWidth }}>
                                <ExitToAppIcon />
                            </ListItemIcon>

                            {!collapsed && (
                                <ListItemText
                                    sx={{ m: 0 }}
                                    primary={intl.formatMessage({
                                        defaultMessage: 'Logout',
                                    })}
                                />
                            )}
                        </ListItemButton>
                    </ListItem>
                </List>
            </form>
        </Box>
    )
}
